import { useEffect } from 'react';
import orgLayerConfigSync, { type OrgLayerConfigSyncController } from '~/store2/orgLayerConfigSync';
import { bindVisibilityAndUnloadFlush, type ListenerTarget } from './bindVisibilityAndUnloadFlush';

/**
 * Wire the org sync controller's lifecycle flushes and return the teardown. Extracted from the
 * page effect so the wiring is unit-testable without a React renderer — mirrors
 * `wireLayerConfigSync` (the per-user twin).
 *
 * Flushes via `flushNow()` on `visibilitychange`→hidden and `beforeunload`. Unlike the per-user
 * wiring there is no router `onBeforeNavigate` boundary: the org sync is page-scoped (the page
 * owns start/stop), so in-app navigation unmounts the page and the teardown flush already runs —
 * the only uncovered case is a hard tab-close / refresh, which these two events handle.
 *
 * Every flush is internally gated on `canPersist` + dirty, so this wiring is inert until a
 * successful hydrate arms persistence.
 */
export function wireOrgLayerConfigSync(
  controller: Pick<OrgLayerConfigSyncController, 'start' | 'stop' | 'flushNow'> = orgLayerConfigSync,
  documentTarget: ListenerTarget & { hidden: boolean } = document,
  windowTarget: ListenerTarget = window,
): () => void {
  controller.start();

  const unbind = bindVisibilityAndUnloadFlush(controller, documentTarget, windowTarget);

  return () => {
    unbind();
    controller.flushNow();
    controller.stop();
  };
}

/**
 * Start the org sync + lifecycle flush listeners for the page's lifetime; tear down on unmount.
 * `enabled` lets the page withhold wiring until access is resolved (a user about to be
 * redirected should never start it) — defaults to `true` for backward compatibility.
 */
export function useOrgLayerConfigSync(enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    return wireOrgLayerConfigSync();
  }, [enabled]);
}
